import React from 'react'
import lowerCase from 'lodash/lowerCase'
import upperFirst from 'lodash/upperFirst'
import Modal from '../../shared/modal'
import Icon from '../../shared/icon'
import { t } from '../../../i18n'
import { useCatalogue } from '../CatalogueProvider'
import { findItem } from '../catalogueSelectors'
import { categoryOptionValue } from '../item_form/categoryFormValues'
import { CatalogueItemTypeIndicator, InforSyncBadge, itemTypeActionLabel, itemTypeLabel } from '../item_form/CatalogueFormControls'
import { orderProviderExternalId, orderProviderLabel } from '../item_form/productFormValues'
import { OrderProvider, ProductRow, WeightUnit } from '../catalogueTypes'

const scope = 'product'

type DetailValue = string | number | null | undefined

const formatDetail = (value: DetailValue): React.ReactNode => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return value
  return Number.isNaN(Number(value)) ? upperFirst(lowerCase(value)) : value
}

const DetailRow = ({ label, value, className = '' }: { label: string; value: DetailValue; className?: string }) => (
  <div className={`show-input ${className}`}>
    <b>{label}</b>
    <span>{formatDetail(value)}</span>
  </div>
)

const OrderProviderDetails = ({ product, orderProviders }: { product: ProductRow; orderProviders: OrderProvider[] }) => {
  if (orderProviders.length === 0) return null

  return (
    <div className="order-providers">
      {orderProviders.map((provider) => (
        <div key={provider.provider_type}>
          <div className="show-input">
            <b>{orderProviderLabel(provider)}</b>
            <span>
              <InforSyncBadge product={product} inforExternalId={product.infor_external_id} isModifier={!!product.is_modifier} />{' '}
              {product.is_modifier ? orderProviderExternalId(product, provider) : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

interface ProductDetailsProps {
  product: ProductRow
  categoryLabel: string
  weightUnit: WeightUnit
  orderProviders: OrderProvider[]
}

const ProductDetails = ({ product, categoryLabel, weightUnit, orderProviders }: ProductDetailsProps) => (
  <>
    <DetailRow label={t('category')} value={categoryLabel} />
    <div className="big-show-input">
      {product.file_url ? <img src={product.file_url} /> : <Icon name="categoryPlaceholder" className="big-image" />}
      <h4>{product.name}</h4>
    </div>
    <DetailRow label={t('feeder_type', { scope })} value={product.feeder_type} />
    <DetailRow label={t('feeder_subtype', { scope })} value={product.feeder_subtype} />
    <DetailRow label={t('fraction', { scope })} value={product.fraction?.name} />
    <DetailRow label={t('unit', { scope })} value={product.unit?.name} />
    {product.feeder_type === 'liquid' ? (
      <DetailRow label={t('sauce_dosing_mode', { scope })} value={product.sauce_dosing_mode?.name} />
    ) : (
      <DetailRow label={t('feeder_dosing_mode', { scope })} value={product.feeder_dosing_mode?.name} />
    )}
    <DetailRow label={t('dosing_weight_step', { scope })} value={product.dosing_weight_step} />
    <DetailRow label={t('alert', { scope })} value={product.alert} />
    <DetailRow label={t('kcal', { scope })} value={product.kcal} />
    <DetailRow label={t('kj', { scope })} value={product.kjoul} />
    <DetailRow label={t('alergens', { scope })} value={product.alergens.map((alergen) => alergen.name).join(', ')} />
    <DetailRow label={t('processing_loss', { scope })} value={product.processing_loss} />
    <DetailRow
      label={t(weightUnit === 'g' ? 'conversion_to_liters' : 'conversion_to_fluid_ounces', { scope })}
      value={product.volume_to_weight_multiplier}
    />
    <DetailRow label={t('dosing_time', { scope })} value={product.dosing_time} />
    <DetailRow label={t('max_container_amount', { scope })} value={product.max_container_amount} />
    <DetailRow
      label={t('is_modifier', { scope })}
      value={product.is_modifier ? t('product_is_modifier', { scope }) : t('product_is_not_modifier', { scope })}
    />
    <OrderProviderDetails product={product} orderProviders={orderProviders} />
    <DetailRow label={t('notes', { scope })} value={product.notes} className="text-show-input" />
  </>
)

const ProductDetailsDialog = () => {
  const { state, meta } = useCatalogue()
  const { show, rows, roots, categoryOptions } = state

  if (!show) return null
  const item = findItem(rows, roots, show)
  if (!item || item.class_name !== 'Product') return null

  return (
    <Modal onClose={meta.go.hide} widen={true} headerContent={<h3>{itemTypeLabel('product')}</h3>}>
      <div className="pb-2 modal-form catalogue-form">
        <CatalogueItemTypeIndicator value="product" showDivider={false} />
        <ProductDetails
          product={item}
          categoryLabel={categoryOptionValue(categoryOptions, item.product_category_id)?.label ?? ''}
          weightUnit={meta.weightUnit}
          orderProviders={meta.orderProviders}
        />
        <button type="button" className="btn btn-primary mt-3" onClick={() => meta.go.edit(item)}>
          {itemTypeActionLabel('edit', 'product')}
        </button>
      </div>
    </Modal>
  )
}

export default ProductDetailsDialog
